import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { atlasAskStream, canSee, consoleAnswerLinks, guideFacts } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { GUIDE_FACTS, GUIDE_HINT, GUIDE_NEWS, TUTORIALS, type GuideRole } from '../../content/guideData';
import { sx } from '../../ui/designStyles';
import { noDash } from '../atlas/common';
import { guideHows, guideLooseSearch, guideScreens, guideSearch, helpPages, helpQuestion, topicSlug, type GuideSearchHit } from './guideVals';
import { useGuideProgress } from './useGuideProgress';

/**
 * The staff guide (design isStaff): hero search ("/" focuses, arrows and Enter), Popular
 * three, the setup ring with Continue, the numbered track, How do I grouped by page, the
 * facts block (Operator and Sales), and a side panel for a step or an answer with Open in
 * console, Done and Next step. Ticks are the server's (useGuideProgress).
 */
type Sel = { k: 'none' | 's' | 'h'; i: number };

/**
 * A free question the guide's own search could not match: Atlas's answer (asking, then
 * answered with the pages it names), or the closest steps and answers in this guide when
 * the role has no Atlas or Atlas could not answer.
 */
type Help =
  | { q: string; state: 'asking'; matches: GuideSearchHit[] }
  | { q: string; state: 'answered'; text: string; pages: { label: string; path: string }[]; matches: GuideSearchHit[] }
  | { q: string; state: 'matches'; why: 'role' | 'failed'; matches: GuideSearchHit[] };

const S = {
  wrap: 'min-height:100vh;box-sizing:border-box;padding:16px 28px 28px;display:flex;justify-content:center;',
  col: 'width:100%;max-width:1180px;display:flex;flex-direction:column;gap:22px;',
  top: 'display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;',
  logo: 'font-size:20px;font-weight:800;letter-spacing:-.02em;',
  kick: 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#2F5C97;font-weight:700;',
  manual: 'display:inline-flex;align-items:center;min-height:32px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;color:#2F5C97;white-space:nowrap;',
  back: 'display:inline-flex;align-items:center;min-height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;color:#3C454F;border:1px solid rgba(34,39,46,.18);white-space:nowrap;',
  hero: 'flex:0 0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:14px 0 6px;animation:gRiseA .45s cubic-bezier(.22,.61,.36,1) both;',
  pill: 'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:999px;font-size:14px;font-weight:700;color:#2F5C97;background:rgba(79,138,214,.12);',
  h: 'font-size:40px;font-weight:800;letter-spacing:-.032em;line-height:1.05;margin-top:12px;',
  lead: 'font-size:16px;color:#3C454F;margin-top:8px;text-wrap:pretty;',
  qWrap: 'position:relative;width:100%;max-width:640px;margin-top:18px;text-align:left;',
  qIcon: 'position:absolute;left:16px;top:17px;',
  q: 'display:block;width:100%;box-sizing:border-box;height:52px;padding:0 48px 0 44px;border-radius:12px;border:1.5px solid rgba(34,39,46,.22);background:#FCFAF4;font:inherit;font-size:16px;color:#22272E;box-shadow:0 6px 20px rgba(34,39,46,.06);',
  slash: 'position:absolute;right:14px;top:15px;display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:22px;border-radius:5px;border:1px solid rgba(34,39,46,.2);font-size:14px;font-weight:700;color:#54606C;',
  drop: 'position:absolute;left:0;right:0;top:58px;z-index:20;background:#FCFAF4;border:1px solid rgba(34,39,46,.18);border-radius:12px;box-shadow:0 16px 40px rgba(34,39,46,.18);padding:6px;display:flex;flex-direction:column;',
  dropHint: 'padding:6px 10px 3px;font-size:14px;color:#54606C;border-top:1px solid rgba(34,39,46,.08);margin-top:3px;',
  quickRow: 'display:flex;align-items:center;justify-content:center;gap:8px;flex-wrap:nowrap;margin-top:12px;max-width:100%;overflow:hidden;height:32px;',
  quick: 'display:inline-flex;align-items:center;flex:0 0 auto;white-space:nowrap;height:30px;padding:0 12px;border-radius:999px;font-size:16px;font-weight:600;text-decoration:none;color:#2F5C97;border:1px solid rgba(47,92,151,.3);',
  steps: 'flex:0 0 auto;display:flex;flex-direction:column;gap:18px;padding:18px 0 20px;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);animation:gRiseB .5s .08s cubic-bezier(.22,.61,.36,1) both;',
  stepsTop: 'display:flex;align-items:center;gap:12px 16px;flex-wrap:wrap;',
  ring: 'position:relative;width:52px;height:52px;flex:0 0 52px;',
  ringText: 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;',
  stepsHead: 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;',
  prog: 'font-size:20px;font-weight:800;letter-spacing:-.018em;margin-top:2px;',
  reset: 'font-size:16px;color:#54606C;text-decoration:none;white-space:nowrap;',
  news: 'flex:0 0 auto;display:flex;align-items:baseline;gap:9px;font-size:16px;color:#3C454F;line-height:1.45;padding-top:4px;',
  newTag: 'flex:0 0 auto;font-size:14px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#1F6B52;background:rgba(47,138,111,.13);border-radius:4px;padding:2px 6px;',
  howH: 'font-size:22px;font-weight:800;letter-spacing:-.02em;',
  howGrid: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:18px 26px;',
  howG: 'min-width:0;border-top:2px solid #2F5C97;padding-top:9px;',
  howGName: 'font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#2F5C97;font-weight:800;padding-bottom:4px;',
  how: 'display:flex;align-items:center;gap:8px;padding:8px 6px;margin:0 -6px;border-radius:7px;font-size:14px;font-weight:600;line-height:1.3;text-decoration:none;color:#22272E;border-bottom:1px solid rgba(34,39,46,.08);',
  fact: 'padding:8px 0;border-top:1px solid rgba(34,39,46,.1);font-size:16px;line-height:1.4;',
  foot: 'font-size:16px;color:#54606C;padding-top:10px;border-top:1px solid rgba(34,39,46,.12);',
  scrim: 'position:fixed;inset:0;z-index:40;background:rgba(34,39,46,.26);animation:gFade .25s ease both;',
  drawer: 'position:fixed;top:0;right:0;bottom:0;z-index:41;width:min(600px,100%);box-sizing:border-box;background:#F7F3EA;border-left:1px solid rgba(34,39,46,.16);box-shadow:-24px 0 60px rgba(34,39,46,.16);overflow-y:auto;padding:20px 30px 28px;animation:gSlide .32s cubic-bezier(.22,.61,.36,1) both;',
  dKick: 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#2F5C97;font-weight:700;',
  dDone: 'font-size:14px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#1F6B52;background:rgba(47,138,111,.13);border-radius:4px;padding:2px 6px;',
  dX: 'display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:8px;font-size:18px;text-decoration:none;color:#3C454F;',
  dTitle: 'font-size:28px;font-weight:800;letter-spacing:-.024em;line-height:1.14;margin-top:6px;text-wrap:balance;',
  dLead: 'font-size:16.5px;font-weight:600;color:#2F5C97;margin-top:10px;line-height:1.45;text-wrap:pretty;',
  dBody: 'font-size:15.5px;color:#3C454F;line-height:1.6;margin-top:10px;text-wrap:pretty;',
  dPairs: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:0 22px;margin-top:14px;',
  dPair: 'padding:10px 0;border-top:1px solid rgba(34,39,46,.12);',
  dPoints: 'display:flex;flex-direction:column;gap:8px;margin-top:14px;',
  dPoint: 'display:flex;gap:10px;font-size:16px;color:#3C454F;line-height:1.5;',
  dDot: 'width:6px;height:6px;border-radius:50%;background:#4F8AD6;flex:0 0 6px;margin-top:.55em;',
  dNote: 'font-size:16px;color:#54606C;line-height:1.55;margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.12);text-wrap:pretty;',
  dActs: 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:20px;',
  dNext: 'display:inline-flex;align-items:center;height:38px;padding:0 10px;font-size:14px;font-weight:700;text-decoration:none;color:#2F5C97;white-space:nowrap;',
  dErr: 'font-size:16px;color:#B3261E;margin-top:10px;line-height:1.45;',
};

function useWidth() {
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1200));
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

export function GuideStaff({ raw, name, R }: { raw: string; name: string; R: GuideRole }) {
  const { client } = useSession();
  const factsQ = useQuery((signal) => guideFacts(client, { signal }), [client], { enabled: name === 'Operator' || name === 'Sales' });
  const { role } = useConsoleRole();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const w = useWidth();
  const wide = w >= 1120;
  const mid = w >= 760;
  const qRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [qi, setQi] = useState(0);
  const [qFocus, setQFocus] = useState(false);
  // The search box closes its suggestions a moment after it loses focus (so a click on one
  // lands first). That timer is cancelled when the page closes, so it never fires afterwards.
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);
  const [anim, setAnim] = useState(0);
  const prog = useGuideProgress(name);
  const done = prog.done;

  const { screens } = useMemo(() => guideScreens(R, TUTORIALS, role), [R, role]);
  const hows = useMemo(() => guideHows(R, role), [R, role]);
  const tot = screens.length;
  const doneN = screens.filter((s) => done[s.title]).length;
  const nextI = screens.findIndex((s) => !done[s.title]);

  // The open item lives in the URL (?topic=<slug>), so a link can open an answer.
  const topic = params.get('topic');
  const sel: Sel = useMemo(() => {
    if (!topic) return { k: 'none', i: -1 };
    const si = screens.findIndex((s) => topicSlug(s.title) === topic);
    if (si >= 0) return { k: 's', i: si };
    const hi = hows.findIndex((h) => topicSlug(h.q) === topic);
    return hi >= 0 ? { k: 'h', i: hi } : { k: 'none', i: -1 };
  }, [topic, screens, hows]);
  const setTopic = (slug: string | null) => {
    const next = new URLSearchParams(params);
    if (slug) next.set('topic', slug);
    else next.delete('topic');
    setParams(next, { replace: true });
  };
  const pick = (k: 's' | 'h', i: number) => {
    const t = k === 's' ? screens[i]?.title : hows[i]?.q;
    if (!t) return;
    setAnim((a) => a + 1);
    setQ('');
    setQFocus(false);
    setTopic(topicSlug(t));
  };
  const close = () => setTopic(null);

  // A free question (no step or answer matched every word): Atlas answers it as product help
  // through the same governed route as the Atlas drawer (POST /api/atlas/ask), for the roles
  // that may use Atlas (the drawer's rule, canSee 'atlas'; the server checks again). Any other
  // role, and any question Atlas refuses, withholds or fails, gets the closest guide entries.
  const mayAsk = canSee(role, 'atlas');
  const [help, setHelp] = useState<Help | null>(null);
  const askSeq = useRef(0);
  const askAbort = useRef<AbortController | null>(null);
  useEffect(() => () => askAbort.current?.abort(), []);
  const closeHelp = () => {
    askSeq.current++;
    askAbort.current?.abort();
    setHelp(null);
  };
  const askHelp = async (question: string) => {
    const qt = question.trim();
    if (!qt) return;
    const matches = guideLooseSearch(screens, hows, qt);
    setQ('');
    setQFocus(false);
    if (topic) setTopic(null);
    askAbort.current?.abort();
    const seq = ++askSeq.current;
    if (!mayAsk) {
      setHelp({ q: qt, state: 'matches', why: 'role', matches });
      return;
    }
    setHelp({ q: qt, state: 'asking', matches });
    const ac = new AbortController();
    askAbort.current = ac;
    try {
      const turn = await atlasAskStream(client, { question: helpQuestion(qt, name, role) }, { signal: ac.signal });
      if (seq !== askSeq.current) return;
      const text = noDash(turn.text.trim());
      if (turn.withheld || !text) setHelp({ q: qt, state: 'matches', why: 'failed', matches });
      else setHelp({ q: qt, state: 'answered', text, pages: helpPages(text, consoleAnswerLinks(turn, role), role), matches });
    } catch {
      if (seq === askSeq.current) setHelp({ q: qt, state: 'matches', why: 'failed', matches });
    }
  };
  const pickMatch = (m: GuideSearchHit) => {
    closeHelp();
    pick(m.k, m.i);
  };

  useEffect(() => {
    const kd = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && help) {
        closeHelp();
        return;
      }
      if (e.key === 'Escape' && params.get('topic')) {
        close();
        return;
      }
      if (e.key === '/' && document.activeElement !== qRef.current && qRef.current) {
        e.preventDefault();
        qRef.current.focus();
      }
    };
    window.addEventListener('keydown', kd);
    return () => window.removeEventListener('keydown', kd);
  });

  const toggle = (t: string) => prog.setStep(t, !done[t]);
  const markAndAdvance = async (i: number) => {
    const t = screens[i].title;
    const ok = await prog.setStep(t, true);
    if (!ok) return;
    const d = { ...done, [t]: 'now' };
    const nx = screens.findIndex((x, j) => j !== i && !d[x.title]);
    pick('s', nx < 0 ? i : nx);
  };

  // groups of answers, by the page each opens
  const groups: { name: string; items: typeof hows }[] = [];
  hows.forEach((h) => {
    let g = groups.find((x) => x.name === h.group);
    if (!g) {
      g = { name: h.group, items: [] };
      groups.push(g);
    }
    g.items.push(h);
  });

  // search
  const res = guideSearch(screens, hows, q);
  const qiC = Math.min(qi, Math.max(0, res.length - 1));
  const qq = q.trim();

  // hero
  const ns = nextI >= 0 ? screens[nextI] : null;
  const C0 = 150.8;
  const frac = tot ? doneN / tot : 0;
  const all = tot > 0 && doneN === tot;
  // The facts come from the API (GET /facts: the catalog count, and the owner's edits),
  // falling back to the bundled file when the server cannot be reached.
  const F = { ...GUIDE_FACTS, ...(factsQ.data ?? {}) };
  const facts = [
    ['Facilities scored', F.facilitiesScored ? `${F.facilitiesScored} US care facilities, ingested and scored` : ''],
    ['Opening conversations', F.facilitiesOpening ? `${F.facilitiesOpening} facilities. Today’s real count comes from the CRM.` : ''],
    ['Where we start', (F.startMarkets || []).join(' and ')],
    ['First pilots', F.firstPilots || ''],
    ['Founding offer', F.founding ? `First ${F.founding.cohort} facilities, ${F.founding.pilotDays} days free, code ${F.founding.code}. No result is quoted before day ${F.founding.minDaysForResults}.` : ''],
    ['Team', (F.team || []).map((t) => `${t.name} (${t.title})`).join(', ')],
    ['Layer 3 is called', F.layers?.l3 || ''],
  ].filter((f) => f[1]);
  const showFacts = name === 'Operator' || name === 'Sales';
  const bodyGrid = (name === 'Operator' || name === 'Sales') && wide ? 'display:grid;grid-template-columns:minmax(0,1fr) minmax(260px,320px);gap:24px 40px;align-items:start;' : 'display:flex;flex-direction:column;gap:26px;';
  const animCss = `animation:${anim % 2 ? 'gRiseA' : 'gRiseB'} .34s cubic-bezier(.22,.61,.36,1) both;`;
  const readOnly = prog.readOnly;

  // detail
  let D: {
    kicker: string; title: string; lead: string; body: string; pairs: { k: string; v: string }[]; points: string[]; note: string; go: string;
    isStep: boolean; isDone: boolean; hasNext: boolean; nextLabel: string; onNext: () => void; onDone?: () => void; doneLabel?: string; doneStyle?: string;
  } | null = null;
  if (sel.k === 'h' && hows[sel.i]) {
    const h = hows[sel.i];
    D = {
      kicker: `How do I · ${h.group}`, title: h.q, lead: '', body: h.a, pairs: [], points: [],
      note: !tot && R.manual ? 'New here? Your onboarding is the Sales manual (top right).' : '', go: h.href, isStep: false, isDone: false,
      hasNext: nextI >= 0, nextLabel: 'Back to your next step →', onNext: () => pick('s', nextI),
    };
  } else if (sel.k === 's') {
    const i = Math.max(0, Math.min(sel.i, tot - 1));
    const s = screens[i];
    if (s) {
      const dn = !!done[s.title];
      D = {
        kicker: `${i === nextI ? 'Your next step · ' : ''}Step ${i + 1} of ${tot}`, title: s.title, lead: s.lead, body: s.body, pairs: s.pairs, points: s.points, note: s.note, go: s.go,
        isStep: true, isDone: dn, doneLabel: dn ? 'Undo done' : 'Done',
        doneStyle: 'display:inline-flex;align-items:center;height:36px;padding:0 14px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (dn ? 'color:#54606C;border:1px solid rgba(34,39,46,.2);' : 'color:#1F6B52;border:1px solid rgba(31,107,82,.45);'),
        onDone: () => (dn ? toggle(s.title) : markAndAdvance(i)),
        hasNext: i < tot - 1, nextLabel: 'Next step →', onNext: () => pick('s', i + 1),
      };
    }
  }
  const dGoStyle = 'display:inline-flex;align-items:center;height:38px;padding:0 16px;border-radius:9px;font-size:14px;font-weight:700;text-decoration:none;color:#fff;background:#2F5C97;white-space:nowrap;' + (D && D.isStep && !D.isDone ? 'animation:gGlow 2.4s ease-in-out infinite;' : '');

  const contStyle = 'display:inline-flex;align-items:center;min-height:40px;box-sizing:border-box;padding:6px 18px;border-radius:10px;font-size:14px;font-weight:700;line-height:1.3;text-decoration:none;max-width:100%;overflow-wrap:break-word;' + (ns ? 'color:#fff;background:#2F5C97;animation:gGlow 2.4s ease-in-out infinite;' : 'color:#1F6B52;background:rgba(47,138,111,.13);');
  const trackStyle = mid ? `display:grid;grid-template-columns:repeat(${Math.max(1, tot)},minmax(0,1fr));gap:0 6px;` : 'display:flex;flex-direction:column;gap:12px;';

  return (
    <>
      <div data-screen-label="Guide · staff" style={sx(S.wrap)}>
        <div style={sx(S.col)}>
          <div style={sx(S.top)}>
            <span style={sx(S.logo)}>aithora</span>
            <span style={sx(S.kick)}>Guide</span>
            <span style={sx('flex:1 1 20px;')} />
            {R.manual && (
              // The sales manual is its own document; the Sales Kit is where the console opens it.
              <Link to="/sales?tab=Sales%20Kit" className="g-manual" style={sx(S.manual)}>
                {'Sales manual →'}
              </Link>
            )}
            <Link to="/" className="noprint g-back" style={sx(S.back)}>
              {'← Back to console'}
            </Link>
          </div>

          <div style={sx(S.hero)}>
            <span style={sx(S.pill)}>{raw}</span>
            <div style={sx(S.h)}>How can we help?</div>
            <div style={sx(S.lead)}>{R.lead}</div>
            <div className="noprint" style={sx(S.qWrap)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#54606C" strokeWidth="2" strokeLinecap="round" style={sx(S.qIcon)} aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
              <input
                ref={qRef}
                className="g-q"
                value={q}
                aria-label="Search this guide"
                onChange={(e) => {
                  setQ(e.target.value);
                  setQi(0);
                  setQFocus(true);
                }}
                onFocus={() => setQFocus(true)}
                onBlur={() => { blurTimer.current = setTimeout(() => setQFocus(false), 120); }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setQi(Math.min(res.length - 1, qiC + 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setQi(Math.max(0, qiC - 1));
                  } else if (e.key === 'Enter' && res[qiC]) {
                    e.preventDefault();
                    pick(res[qiC].k, res[qiC].i);
                    e.currentTarget.blur();
                  } else if (e.key === 'Enter' && qq) {
                    e.preventDefault();
                    e.currentTarget.blur();
                    void askHelp(qq);
                  } else if (e.key === 'Escape') {
                    setQ('');
                    setQFocus(false);
                    e.currentTarget.blur();
                  }
                }}
                placeholder={GUIDE_HINT[name] || 'Search this guide…'}
                style={sx(S.q)}
              />
              <span style={sx(S.slash)}>/</span>
              {!!qq && qFocus && (
                <div style={sx(S.drop)} role="listbox" aria-label="Search results">
                  {res.map((r, j) => (
                    <a
                      key={`${r.k}${r.i}`}
                      href="#"
                      role="option"
                      aria-selected={j === qiC}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pick(r.k, r.i);
                        qRef.current?.blur();
                      }}
                      style={sx('display:flex;align-items:center;gap:9px;padding:8px 9px;border-radius:7px;text-decoration:none;' + (j === qiC ? 'background:rgba(79,138,214,.13);' : ''))}
                    >
                      <span
                        style={sx(
                          'flex:0 0 auto;min-width:34px;text-align:center;font-size:14px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;border-radius:4px;padding:2px 5px;' +
                            (r.k === 's' ? 'color:#2F5C97;background:rgba(79,138,214,.14);' : 'color:#1F6B52;background:rgba(47,138,111,.13);'),
                        )}
                      >
                        {r.tag}
                      </span>
                      <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;font-weight:600;color:#22272E;overflow-wrap:break-word;')}>{r.title}</span>
                      <span style={sx('flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;')}>{r.where}</span>
                    </a>
                  ))}
                  {res.length === 0 && (
                    <a
                      href="#"
                      role="option"
                      aria-selected
                      data-testid="guide-ask"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        qRef.current?.blur();
                        void askHelp(qq);
                      }}
                      style={sx('display:flex;align-items:center;gap:9px;padding:8px 9px;border-radius:7px;text-decoration:none;background:rgba(79,138,214,.13);')}
                    >
                      <span style={sx('flex:0 0 auto;font-size:14px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;border-radius:4px;padding:2px 5px;color:#2F5C97;background:rgba(79,138,214,.14);')}>
                        {mayAsk ? 'Atlas' : 'Guide'}
                      </span>
                      <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;font-weight:600;color:#22272E;overflow-wrap:break-word;')}>
                        <span>{mayAsk ? 'Ask Atlas' : 'Show the closest answers'}</span>
                        <span style={sx('font-weight:400;color:#54606C;')}>{`: “${qq}”`}</span>
                      </span>
                    </a>
                  )}
                  <div style={sx(S.dropHint)}>{res.length ? '↑↓ to move · Enter to open · Esc to close' : mayAsk ? 'Enter to ask Atlas · Esc to close' : 'Enter to see the closest answers · Esc to close'}</div>
                </div>
              )}
            </div>
            <div className="noprint" style={sx(S.quickRow)}>
              <span style={sx('font-size:16px;color:#54606C;')}>Popular:</span>
              {hows.slice(0, 3).map((h) => (
                <button key={h.i} type="button" className="dlink g-pop" style={sx(S.quick)} onClick={() => pick('h', h.i)}>
                  {h.q}
                </button>
              ))}
            </div>
          </div>

          {tot > 0 && (
            <div style={sx(S.steps)}>
              <div style={sx(S.stepsTop)}>
                <div style={sx(S.ring)}>
                  <svg width="52" height="52" viewBox="0 0 60 60" style={sx('display:block;transform:rotate(-90deg);')} aria-hidden="true">
                    <circle cx="30" cy="30" r="24" fill="none" stroke="rgba(34,39,46,.1)" strokeWidth="7" />
                    <circle
                      cx="30" cy="30" r="24" fill="none" stroke={all ? '#1F6B52' : '#4F8AD6'} strokeWidth="7" strokeLinecap="round" strokeDasharray="150.8"
                      strokeDashoffset={String(C0 * (1 - frac))}
                      style={sx('transition:stroke-dashoffset .6s cubic-bezier(.22,.61,.36,1),stroke .3s;')}
                    />
                  </svg>
                  <span style={sx(S.ringText)}>{tot ? `${doneN}/${tot}` : '–'}</span>
                </div>
                <div style={sx('min-width:0;flex:1 1 200px;')}>
                  <div style={sx(S.stepsHead)}>{R.tutorial ? 'First 10 minutes' : 'Setup'}</div>
                  <div style={sx(S.prog)}>{all ? `All ${tot} done` : `${doneN} of ${tot} done`}</div>
                </div>
                {doneN > 0 && !readOnly && (
                  <button type="button" className="dlink g-reset" style={sx(S.reset)} onClick={() => prog.reset().then((ok) => ok && close())}>
                    Start over
                  </button>
                )}
                <button type="button" className="dlink" style={sx(contStyle)} onClick={() => nextI >= 0 && pick('s', nextI)}>
                  {ns ? `Continue: ${ns.title} →` : 'All set ✓'}
                </button>
              </div>
              <div style={sx(trackStyle)}>
                {screens.map((s, i) => {
                  const dn = !!done[s.title];
                  const cur = i === nextI;
                  const nodeStyle =
                    'position:relative;z-index:1;display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;box-sizing:border-box;border-radius:50%;font-size:16px;font-weight:800;text-decoration:none;flex:0 0 32px;font-variant-numeric:tabular-nums;' +
                    (dn ? 'background:#1F6B52;color:#fff;animation:gPop .35s ease both;' : cur ? 'background:#2F5C97;color:#fff;animation:gGlow 2.4s ease-in-out infinite;' : 'background:#F2EDE2;border:2px solid rgba(34,39,46,.28);color:#54606C;');
                  const titleStyle =
                    'font-size:16px;line-height:1.3;text-decoration:none;' +
                    (cur ? 'font-weight:800;color:#22272E;' : dn ? 'font-weight:600;color:#54606C;' : 'font-weight:600;color:#22272E;') +
                    (mid ? 'overflow-wrap:break-word;' : '');
                  return (
                    <div key={s.title} style={sx(mid ? 'position:relative;display:flex;flex-direction:column;align-items:center;gap:9px;min-width:0;text-align:center;' : 'position:relative;display:flex;align-items:center;gap:12px;min-width:0;')}>
                      {mid && i < tot - 1 && (
                        <span style={sx('position:absolute;top:15px;left:calc(50% + 20px);right:calc(-50% + 14px);height:3px;border-radius:2px;background:' + (dn ? '#1F6B52' : 'rgba(34,39,46,.14)') + ';transition:background .4s;')} />
                      )}
                      <button type="button" className="dlink" title={s.title} aria-label={`Step ${i + 1}: ${s.title}${dn ? ', done' : ''}`} style={sx(nodeStyle)} onClick={() => pick('s', i)}>
                        {dn ? '✓' : String(i + 1)}
                      </button>
                      <button type="button" className="dlink g-ttl" style={sx(titleStyle)} onClick={() => pick('s', i)} tabIndex={-1}>
                        {s.title}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {GUIDE_NEWS[name] && (
            <div style={sx(S.news)}>
              <span style={sx(S.newTag)}>New</span>
              <span style={sx('min-width:0;text-wrap:pretty;')}>{GUIDE_NEWS[name]}</span>
            </div>
          )}

          <div style={sx(bodyGrid)}>
            <div style={sx('min-width:0;')}>
              <div style={sx('display:flex;align-items:baseline;gap:10px;padding-bottom:10px;')}>
                <span style={sx(S.howH)}>{'How do I…'}</span>
                <span style={sx('font-size:16px;color:#54606C;')}>{`${hows.length}${hows.length === 1 ? ' answer' : ' answers'}, each opens the right page`}</span>
              </div>
              <div style={sx(S.howGrid)}>
                {groups.map((g) => (
                  <div key={g.name} style={sx(S.howG)}>
                    <div style={sx(S.howGName)}>{g.name}</div>
                    {g.items.map((h) => (
                      <button key={h.i} type="button" className="dlink g-how" style={{ ...sx(S.how), width: '100%', textAlign: 'left' } as CSSProperties} onClick={() => pick('h', h.i)}>
                        <span style={sx('flex:1 1 auto;min-width:0;')}>{h.q}</span>
                        <span style={sx('flex:0 0 auto;color:#2F5C97;font-weight:700;')}>{'→'}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            {showFacts && (
              <div style={sx('min-width:0;')}>
                <div style={sx('font-size:22px;font-weight:800;letter-spacing:-.02em;padding-bottom:4px;')}>Facts you may repeat</div>
                <div style={sx('font-size:16px;color:#54606C;padding-bottom:8px;')}>One source. If one changes, it changes everywhere.</div>
                {facts.map((f) => (
                  <div key={f[0]} style={sx(S.fact)}>
                    <div style={sx('font-weight:700;')}>{f[0]}</div>
                    <div style={sx('color:#3C454F;margin-top:1px;')}>{f[1]}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={sx(S.foot)}>
            Still stuck? Press <b>?</b> in the console and ask, or email{' '}
            <a className="g-mail" href="mailto:info@aithoracare.com">
              info@aithoracare.com
            </a>
            .
          </div>
        </div>
      </div>

      {D && (
        <>
          <div onClick={close} style={sx(S.scrim)} aria-hidden="true" />
          <div role="dialog" aria-label={D.title} style={sx(S.drawer)}>
            <div style={sx('display:flex;align-items:center;gap:8px;')}>
              <span style={sx(S.dKick)}>{D.kicker}</span>
              {D.isDone && <span style={sx(S.dDone)}>Done</span>}
              <span style={sx('flex:1;')} />
              <button type="button" className="dlink g-x" aria-label="Close" style={sx(S.dX)} onClick={close}>
                {'✕'}
              </button>
            </div>
            <div key={anim} style={sx(animCss)}>
              <div style={sx(S.dTitle)}>{D.title}</div>
              {!!D.lead && <div style={sx(S.dLead)}>{D.lead}</div>}
              {!!D.body && <div style={sx(S.dBody)}>{D.body}</div>}
              {D.pairs.length > 0 && (
                <div style={sx(S.dPairs)}>
                  {D.pairs.map((p, n) => (
                    <div key={n} style={sx(S.dPair)}>
                      <div style={sx('font-size:14px;font-weight:700;')}>{p.k}</div>
                      <div style={sx('font-size:14px;color:#3C454F;line-height:1.5;margin-top:2px;text-wrap:pretty;')}>{p.v}</div>
                    </div>
                  ))}
                </div>
              )}
              {D.points.length > 0 && (
                <div style={sx(S.dPoints)}>
                  {D.points.map((x, n) => (
                    <div key={n} style={sx(S.dPoint)}>
                      <span style={sx(S.dDot)} />
                      <span>{x}</span>
                    </div>
                  ))}
                </div>
              )}
              {!!D.note && <div style={sx(S.dNote)}>{D.note}</div>}
              <div className="noprint" style={sx(S.dActs)}>
                {!!D.go && (
                  <button type="button" className="dlink g-go" style={sx(dGoStyle)} onClick={() => navigate(D!.go)}>
                    {'Open in console →'}
                  </button>
                )}
                {D.isStep && (
                  <button
                    type="button"
                    className="dlink"
                    style={sx(D.doneStyle!)}
                    disabled={!!readOnly || prog.saving}
                    title={readOnly ?? undefined}
                    aria-disabled={!!readOnly || prog.saving}
                    onClick={() => D!.onDone!()}
                  >
                    {D.doneLabel}
                  </button>
                )}
                {D.hasNext && (
                  <button type="button" className="dlink g-next" style={sx(S.dNext)} onClick={D.onNext}>
                    {D.nextLabel}
                  </button>
                )}
              </div>
              {prog.saveError && <div style={sx(S.dErr)} role="alert">{`Not saved: ${prog.saveError}`}</div>}
            </div>
          </div>
        </>
      )}

      {!D && help && (
        <>
          <div onClick={closeHelp} style={sx(S.scrim)} aria-hidden="true" />
          <div role="dialog" aria-label={help.q} data-testid="guide-help" style={sx(S.drawer)}>
            <div style={sx('display:flex;align-items:center;gap:8px;')}>
              <span style={sx(S.dKick)}>{help.state === 'matches' ? 'Closest answers in this guide' : 'Atlas · product help'}</span>
              <span style={sx('flex:1;')} />
              <button type="button" className="dlink g-x" aria-label="Close" style={sx(S.dX)} onClick={closeHelp}>
                {'✕'}
              </button>
            </div>
            <div style={sx(S.dTitle)}>{help.q}</div>
            {help.state === 'asking' && (
              <div role="status" style={sx(S.dBody)}>
                Atlas is answering…
              </div>
            )}
            {help.state === 'answered' && (
              <>
                <div data-testid="guide-help-answer" style={sx(S.dBody)}>
                  {help.text}
                </div>
                {help.pages.length > 0 && (
                  <div className="noprint" style={sx(S.dActs)}>
                    {help.pages.map((p, n) => (
                      <button key={p.path} type="button" className="dlink g-go" style={sx(n === 0 ? dGoStyle : S.dNext)} onClick={() => navigate(p.path)}>
                        <span>{p.label}</span>
                        {' →'}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {help.state === 'matches' && (
              <div style={sx(S.dBody)}>{help.why === 'role' ? 'These parts of your guide come closest.' : 'Atlas could not answer this one. These parts of your guide come closest.'}</div>
            )}
            {help.state !== 'asking' && (help.state === 'matches' || help.matches.length > 0) && (
              <div style={sx('display:flex;flex-direction:column;margin-top:14px;')}>
                {help.state === 'answered' && <div style={sx(S.stepsHead)}>Also in your guide</div>}
                {help.matches.map((m) => (
                  <button key={`${m.k}${m.i}`} type="button" className="dlink g-how" style={{ ...sx(S.how), width: '100%', textAlign: 'left' } as CSSProperties} onClick={() => pickMatch(m)}>
                    <span style={sx('flex:1 1 auto;min-width:0;')}>{m.title}</span>
                    <span style={sx('flex:0 0 auto;font-size:14px;color:#54606C;font-weight:400;')}>{m.where}</span>
                  </button>
                ))}
                {help.matches.length === 0 && (
                  <div style={sx('font-size:14px;color:#54606C;line-height:1.5;')}>
                    <span>Nothing in this guide comes close.</span> <span>Email</span>{' '}
                    <a className="g-mail" href="mailto:info@aithoracare.com">
                      info@aithoracare.com
                    </a>
                    .
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
